import { GET_FITS_QUERY } from './Queries/Queries.ts';
import {
  FlowContext,
  FlowNode,
  FlowOption,
  GetRearMountedChildBikeSeatsQuery,
  NodeId,
  QueryVariables,
} from './Types/Types.ts';
import { client } from '/Foundation/SitecoreGraphQL/code/clientv2.js';
import { getWindowData } from '../FitAssistantShared/GetWindowData.js';
import { getProductCards, getUniqueProductCardConfirmedFits, setFitStatusForCards } from './Helpers/StatusHelper.ts';
import { IsProductPage } from '../FitAssistantShared/IsProductPage.js';

export const FLOW_START_NODE: NodeId = 'hasRack';

export const getFlowNode = (nodeId: NodeId): FlowNode => FLOW_NODES[nodeId];

const getYesNoOptions = (ctx: FlowContext): FlowOption[] => [
  { value: 'yes', label: ctx.translations?.yes ?? '' },
  { value: 'no', label: ctx.translations?.no ?? '' },
];

const getRackTypeOptions = (ctx: FlowContext): FlowOption[] => [
  { value: 'regular', label: ctx.translations?.regularRack ?? '' },
  { value: 'mik_hd', label: ctx.translations?.mikHDRack ?? '' },
];

export const FLOW_NODES: Record<NodeId, FlowNode> = {
  hasRack: {
    id: 'hasRack',
    kind: 'contentOption',
    answerKey: 'hasRack',
    cmsParameter: 'hasRack',
    cardType: 'horizontal',
    getNext: (_, selectedValue) => selectedValue as NodeId,
  },
  rackType: {
    id: 'rackType',
    kind: 'choice',
    answerKey: 'rackType',
    cmsParameter: 'rackType',
    getOptions: (ctx) => getRackTypeOptions(ctx),
    getNext: (_, selectedValue) => {
      if (selectedValue === 'mik_hd') return 'frameType';

      return 'rackWidth';
    },
  },
  batteryDistance: {
    id: 'batteryDistance',
    kind: 'choice',
    answerKey: 'batteryDistance',
    cmsParameter: 'batteryDistance',
    extraAnswerKeys: ['rackType'],
    getOptions: (ctx) => [
      { value: 'yes', label: ctx.translations?.yes ?? '', extraAnswers: { rackType: 'regular' } },
      { value: 'no', label: ctx.translations?.no ?? '' },
    ],
    getNext: (_, selectedValue) => {
      if (selectedValue === 'yes') {
        return 'rackWidth';
      }
      return 'frameType';
    },
  },
  rackWidth: {
    id: 'rackWidth',
    kind: 'input',
    answerKey: 'rackWidth',
    cmsParameter: 'rackWidth',
    getNext: (_, selectedValue) => {
      const [value, unit] = selectedValue.split(' ');

      const width = Number(value);

      if (unit === 'mm' && (width < 100 || width > 175)) return 'frameType';
      if (unit === 'in' && (width < 4.33 || width > 6.9)) return 'frameType';

      return 'rackTubeSize';
    },
  },
  rackTubeSize: {
    id: 'rackTubeSize',
    kind: 'input',
    answerKey: 'rackTubeSize',
    cmsParameter: 'rackTubeSize',
    getNext: (_, _selectedValue) => 'frameType' as NodeId,
  },
  frameType: {
    id: 'frameType',
    kind: 'contentOption',
    answerKey: 'frameType',
    cmsParameter: 'frameType',
    cardType: 'vertical',
    fetchContextData: async (ctx: FlowContext): Promise<Partial<FlowContext>> => {
      const variables: QueryVariables = {
        language: getWindowData().SitecoreLanguage,
        rackWidth: ctx.answers.rackWidth,
        rackTubeDiameter: ctx.answers.rackTubeSize,
        rackType: ctx.answers.rackType,
        frameType: ctx.answers.frameType,
        ispreview: window._THULEDATA.FitAssistantBikes.IsPreview,
        categoryId: window._THULEDATA.FitAssistantBikes.CategoryId,
      };
      const result = await client.query<GetRearMountedChildBikeSeatsQuery>(GET_FITS_QUERY, variables);
      return { fitQueryResult: result.data.getRearMountedChildBikeSeats.rearMountedChildBikeSeatsResponse };
    },
    onFetchComplete: (ctx: FlowContext): void => {
      if (IsProductPage) return;

      const productCards = getProductCards();
      const confirmedCards = getUniqueProductCardConfirmedFits(productCards, ctx.fitQueryResult?.ProductIds);
      setFitStatusForCards(productCards, confirmedCards);
    },
    getNext: (ctx) => {
      if (!IsProductPage) {
        const productCards = getProductCards();
        const confirmedCards = getUniqueProductCardConfirmedFits(productCards, ctx.fitQueryResult?.ProductIds);
        if (confirmedCards.length > 0) return 'fit';
        return 'noFit';
      } else {
        if (ctx.fitQueryResult?.ProductIds?.find((x) => x === window._THULEDATA.ProductDetail.ProductId)) return 'fit';
        return 'noFit';
      }
    },
  },
  noFit: {
    id: 'noFit',
    kind: 'result',
    cmsParameter: 'nofit',
  },
};
